<script setup>
import { computed, ref, nextTick, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';

const { t } = useI18n();
const store = useStore();

const model = defineModel({ type: Object, required: true });
const textareaRef = ref(null);
const nextVarNumber = ref(1);

const isWhatsApp = computed(() => model.value.inboxType === 'whatsapp');

onMounted(() => {
  store.dispatch('attributes/get');
});

// --- Contact attributes for variable mapping ---
const standardAttrs = [
  { key: 'name', label: t('CAMPAIGN.V2.WIZARD.VARIABLES.CONTACT_ATTRS.NAME') },
  { key: 'email', label: t('CAMPAIGN.V2.WIZARD.VARIABLES.CONTACT_ATTRS.EMAIL') },
  { key: 'phone_number', label: t('CAMPAIGN.V2.WIZARD.VARIABLES.CONTACT_ATTRS.PHONE') },
  { key: 'identifier', label: t('CAMPAIGN.V2.WIZARD.VARIABLES.CONTACT_ATTRS.IDENTIFIER') },
];

const customAttributes = computed(() => {
  const attrs = store.getters['attributes/getAttributesByModel']('contact_attribute') || [];
  return attrs.map(a => ({
    key: `custom_attributes.${a.attribute_key}`,
    label: a.attribute_display_name,
  }));
});

const allContactAttrs = computed(() => [
  ...standardAttrs,
  ...customAttributes.value,
]);

// --- WhatsApp templates ---
const templates = computed(() => {
  if (!isWhatsApp.value) return [];
  const channel = model.value.inbox?.channel || {};
  const allTemplates = channel.message_templates || [];
  return allTemplates.filter(tpl => tpl.status === 'approved');
});

const selectTemplate = tpl => {
  model.value = {
    ...model.value,
    template: tpl,
    templateParams: {
      name: tpl.name,
      namespace: tpl.namespace,
      language: tpl.language,
      category: tpl.category,
    },
  };
};

const isSelectedTemplate = tpl =>
  model.value.template?.name === tpl.name &&
  model.value.template?.language === tpl.language;

const getTemplateBody = tpl => {
  const body = (tpl.components || []).find(c => c.type === 'BODY');
  return body?.text || '';
};

const getTemplateHeader = tpl => {
  const header = (tpl.components || []).find(c => c.type === 'HEADER');
  return header ? `[${header.format}] ${header.text || ''}` : '';
};

// --- API: Rich text helpers ---
const detectedVariables = computed(() => {
  const text = model.value.message || '';
  const matches = text.match(/\{\{(\w+)\}\}/g) || [];
  return [...new Set(matches.map(m => m.replace(/\{\{|\}\}/g, '')))];
});

const updateMessage = event => {
  const newText = event.target.value;
  model.value = { ...model.value, message: newText };
  recalcNextVar(newText);
};

const recalcNextVar = text => {
  const matches = text.match(/\{\{(\d+)\}\}/g) || [];
  const nums = matches.map(m => parseInt(m.replace(/\{\{|\}\}/g, '')));
  nextVarNumber.value = nums.length ? Math.max(...nums) + 1 : 1;
};

// Sync variable config when variables change
watch(
  detectedVariables,
  vars => {
    if (!isWhatsApp.value) {
      const existing = model.value.templateConfig?.body || {};
      const body = {};
      vars.forEach(v => {
        body[v] = existing[v] || { type: 'manual', value: '', key: '' };
      });
      model.value = {
        ...model.value,
        templateConfig: { ...model.value.templateConfig, body },
      };
    }
  },
  { immediate: true }
);

const updateVarType = (varKey, type) => {
  const body = { ...(model.value.templateConfig?.body || {}) };
  body[varKey] = {
    ...body[varKey],
    type,
    value: type === 'manual' ? (body[varKey]?.value || '') : '',
    key: type === 'contact_attr' ? (body[varKey]?.key || '') : '',
  };
  model.value = {
    ...model.value,
    templateConfig: { ...model.value.templateConfig, body },
  };
};

const updateVarValue = (varKey, value) => {
  const body = { ...(model.value.templateConfig?.body || {}) };
  body[varKey] = { ...body[varKey], value };
  model.value = {
    ...model.value,
    templateConfig: { ...model.value.templateConfig, body },
  };
};

const updateVarKey = (varKey, key) => {
  const body = { ...(model.value.templateConfig?.body || {}) };
  body[varKey] = { ...body[varKey], key };
  model.value = {
    ...model.value,
    templateConfig: { ...model.value.templateConfig, body },
  };
};

const varLabel = key => `{{${key}}}`;

const getVarConfig = varKey => {
  return model.value.templateConfig?.body?.[varKey] || { type: 'manual', value: '', key: '' };
};

// --- Toolbar ---
const wrapSelection = (before, after) => {
  const ta = textareaRef.value;
  if (!ta) return;

  const start = ta.selectionStart;
  const end = ta.selectionEnd;
  const text = model.value.message || '';
  const selected = text.substring(start, end);
  const wrapped = `${before}${selected}${after}`;
  const newText = text.substring(0, start) + wrapped + text.substring(end);

  model.value = { ...model.value, message: newText };

  nextTick(() => {
    ta.focus();
    const cursorPos = start + before.length + selected.length + after.length;
    ta.setSelectionRange(cursorPos, cursorPos);
  });
};

const insertAtCursor = textToInsert => {
  const ta = textareaRef.value;
  if (!ta) return;

  const start = ta.selectionStart;
  const text = model.value.message || '';
  const newText = text.substring(0, start) + textToInsert + text.substring(start);

  model.value = { ...model.value, message: newText };
  recalcNextVar(newText);

  nextTick(() => {
    ta.focus();
    const cursorPos = start + textToInsert.length;
    ta.setSelectionRange(cursorPos, cursorPos);
  });
};

const applyBold = () => wrapSelection('*', '*');
const applyItalic = () => wrapSelection('_', '_');
const applyStrikethrough = () => wrapSelection('~', '~');
const applyMonospace = () => wrapSelection('```', '```');

const applyBulletList = () => {
  const ta = textareaRef.value;
  if (!ta) return;
  const start = ta.selectionStart;
  const end = ta.selectionEnd;
  const text = model.value.message || '';
  const selected = text.substring(start, end);

  if (selected) {
    const lines = selected.split('\n').map(line => `• ${line}`).join('\n');
    const newText = text.substring(0, start) + lines + text.substring(end);
    model.value = { ...model.value, message: newText };
  } else {
    insertAtCursor('\n• ');
  }
};

const applyNumberedList = () => {
  const ta = textareaRef.value;
  if (!ta) return;
  const start = ta.selectionStart;
  const end = ta.selectionEnd;
  const text = model.value.message || '';
  const selected = text.substring(start, end);

  if (selected) {
    const lines = selected.split('\n').map((line, i) => `${i + 1}. ${line}`).join('\n');
    const newText = text.substring(0, start) + lines + text.substring(end);
    model.value = { ...model.value, message: newText };
  } else {
    insertAtCursor('\n1. ');
  }
};

const addVariable = () => {
  insertAtCursor(`{{${nextVarNumber.value}}}`);
};

const toolbarButtons = computed(() => [
  { key: 'bold', icon: 'B', title: t('CAMPAIGN.V2.WIZARD.TEMPLATE.TOOLBAR.BOLD'), action: applyBold, class: 'font-bold' },
  { key: 'italic', icon: 'I', title: t('CAMPAIGN.V2.WIZARD.TEMPLATE.TOOLBAR.ITALIC'), action: applyItalic, class: 'italic' },
  { key: 'strike', icon: 'S', title: t('CAMPAIGN.V2.WIZARD.TEMPLATE.TOOLBAR.STRIKETHROUGH'), action: applyStrikethrough, class: 'line-through' },
  { key: 'mono', icon: '</>', title: t('CAMPAIGN.V2.WIZARD.TEMPLATE.TOOLBAR.MONOSPACE'), action: applyMonospace, class: 'font-mono text-xs' },
  { key: 'sep1', separator: true },
  { key: 'bullet', icon: '•', title: t('CAMPAIGN.V2.WIZARD.TEMPLATE.TOOLBAR.BULLET_LIST'), action: applyBulletList, class: '' },
  { key: 'num', icon: '1.', title: t('CAMPAIGN.V2.WIZARD.TEMPLATE.TOOLBAR.NUMBERED_LIST'), action: applyNumberedList, class: '' },
  { key: 'sep2', separator: true },
  { key: 'var', icon: '{ }', title: t('CAMPAIGN.V2.WIZARD.TEMPLATE.TOOLBAR.ADD_VARIABLE'), action: addVariable, class: 'font-mono text-xs text-n-brand', highlight: true },
]);
</script>

<template>
  <div class="max-w-2xl">
    <h2 class="text-base font-medium text-n-slate-12">
      {{
        isWhatsApp
          ? t('CAMPAIGN.V2.WIZARD.TEMPLATE.TITLE_WHATSAPP')
          : t('CAMPAIGN.V2.WIZARD.TEMPLATE.TITLE_API')
      }}
    </h2>
    <p class="mt-1 text-sm text-n-slate-11">
      {{
        isWhatsApp
          ? t('CAMPAIGN.V2.WIZARD.TEMPLATE.DESCRIPTION_WHATSAPP')
          : t('CAMPAIGN.V2.WIZARD.TEMPLATE.DESCRIPTION_API')
      }}
    </p>

    <!-- WhatsApp: Template list -->
    <div v-if="isWhatsApp" class="mt-6">
      <div
        v-if="templates.length === 0"
        class="p-4 text-sm text-n-slate-11 bg-n-alpha-2 rounded-lg"
      >
        {{ t('CAMPAIGN.V2.WIZARD.TEMPLATE.NO_TEMPLATES') }}
      </div>
      <div v-else class="grid gap-3">
        <button
          v-for="tpl in templates"
          :key="`${tpl.name}-${tpl.language}`"
          class="p-4 text-left border rounded-lg transition-colors"
          :class="
            isSelectedTemplate(tpl)
              ? 'border-n-brand bg-n-brand/5'
              : 'border-n-weak hover:border-n-slate-8'
          "
          @click="selectTemplate(tpl)"
        >
          <div class="flex items-center justify-between">
            <div class="font-medium text-n-slate-12">{{ tpl.name }}</div>
            <span class="text-xs text-n-slate-11">{{ tpl.language }}</span>
          </div>
          <div v-if="getTemplateHeader(tpl)" class="mt-1 text-xs text-n-slate-10">
            {{ getTemplateHeader(tpl) }}
          </div>
          <div class="mt-2 text-sm text-n-slate-11 line-clamp-3">
            {{ getTemplateBody(tpl) }}
          </div>
          <div v-if="tpl.category" class="mt-2 text-xs text-n-slate-10">
            {{ tpl.category }}
          </div>
        </button>
      </div>
    </div>

    <!-- API: Rich text editor with toolbar -->
    <div v-else class="mt-6">
      <!-- Toolbar -->
      <div class="flex items-center gap-1 px-2 py-1.5 border border-b-0 rounded-t-lg border-n-weak bg-n-alpha-1">
        <template v-for="btn in toolbarButtons" :key="btn.key">
          <div v-if="btn.separator" class="w-px h-5 mx-1 bg-n-weak" />
          <button
            v-else
            :title="btn.title"
            class="flex items-center justify-center w-8 h-8 text-sm rounded transition-colors"
            :class="[
              btn.class,
              btn.highlight
                ? 'bg-n-brand/10 hover:bg-n-brand/20 text-n-brand'
                : 'text-n-slate-11 hover:bg-n-alpha-3 hover:text-n-slate-12',
            ]"
            @click.prevent="btn.action"
          >
            {{ btn.icon }}
          </button>
        </template>
      </div>

      <!-- Textarea -->
      <textarea
        ref="textareaRef"
        :value="model.message"
        class="w-full h-48 p-4 text-sm border resize-none border-n-weak bg-n-surface-1 text-n-slate-12 placeholder-n-slate-9 focus:border-n-brand focus:outline-none font-mono"
        :class="detectedVariables.length > 0 ? '' : 'rounded-b-lg'"
        :placeholder="t('CAMPAIGN.V2.WIZARD.TEMPLATE.MESSAGE_PLACEHOLDER')"
        @input="updateMessage"
      />

      <!-- Inline variable mapping (appears when variables are detected) -->
      <div
        v-if="detectedVariables.length > 0"
        class="px-4 py-3 space-y-3 border border-t-0 rounded-b-lg border-n-weak bg-n-alpha-1"
      >
        <div class="text-xs font-medium text-n-slate-11 uppercase tracking-wide">
          {{ t('CAMPAIGN.V2.WIZARD.VARIABLES.TITLE') }}
        </div>

        <div
          v-for="varKey in detectedVariables"
          :key="varKey"
          class="flex items-center gap-3"
        >
          <!-- Variable badge -->
          <span class="shrink-0 px-2 py-1 text-xs font-mono font-medium rounded bg-n-brand/10 text-n-brand">
            {{ varLabel(varKey) }}
          </span>

          <!-- Type toggle -->
          <div class="flex items-center gap-2 shrink-0">
            <label class="flex items-center gap-1.5 cursor-pointer">
              <input
                type="radio"
                :name="`var-${varKey}`"
                :checked="getVarConfig(varKey).type === 'manual'"
                class="accent-n-brand w-3.5 h-3.5"
                @change="updateVarType(varKey, 'manual')"
              />
              <span class="text-xs text-n-slate-11">
                {{ t('CAMPAIGN.V2.WIZARD.VARIABLES.TYPE_MANUAL') }}
              </span>
            </label>
            <label class="flex items-center gap-1.5 cursor-pointer">
              <input
                type="radio"
                :name="`var-${varKey}`"
                :checked="getVarConfig(varKey).type === 'contact_attr'"
                class="accent-n-brand w-3.5 h-3.5"
                @change="updateVarType(varKey, 'contact_attr')"
              />
              <span class="text-xs text-n-slate-11">
                {{ t('CAMPAIGN.V2.WIZARD.VARIABLES.TYPE_CONTACT') }}
              </span>
            </label>
          </div>

          <!-- Value input -->
          <input
            v-if="getVarConfig(varKey).type === 'manual'"
            :value="getVarConfig(varKey).value"
            type="text"
            class="flex-1 px-3 py-1.5 text-sm border rounded-lg border-n-weak bg-n-surface-1 text-n-slate-12 placeholder-n-slate-9 focus:border-n-brand focus:outline-none"
            :placeholder="t('CAMPAIGN.V2.WIZARD.VARIABLES.MANUAL_PLACEHOLDER')"
            @input="updateVarValue(varKey, $event.target.value)"
          />
          <select
            v-else
            :value="getVarConfig(varKey).key"
            class="flex-1 px-3 py-1.5 text-sm border rounded-lg border-n-weak bg-n-surface-1 text-n-slate-12 focus:border-n-brand focus:outline-none"
            @change="updateVarKey(varKey, $event.target.value)"
          >
            <option value="" disabled>
              {{ t('CAMPAIGN.V2.WIZARD.VARIABLES.CONTACT_PLACEHOLDER') }}
            </option>
            <option
              v-for="attr in allContactAttrs"
              :key="attr.key"
              :value="attr.key"
            >
              {{ attr.label }}
            </option>
          </select>
        </div>
      </div>

      <p class="mt-2 text-xs text-n-slate-10">
        {{ t('CAMPAIGN.V2.WIZARD.TEMPLATE.FORMATTING_HELP') }}
      </p>
    </div>
  </div>
</template>
